import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Card, Button, Input, Form, message } from 'antd';
import { LockOutlined, SafetyOutlined } from '@ant-design/icons';
import * as adminactions from 'store/actions/AuthActions';

const maskEmail = (email = '') => email.replace(/(.{2})(.*)(@.*)/, '$1***$3');

// Re-verifies the logged-in user before showing sensitive data.
// 2FA users verify with an emailed code (with a password fallback); others use their password.
// `embedded` renders just the content (e.g. inside a Modal) instead of a centered page card.
const VerifyIdentityCard = ({ title, description, submitLabel = 'Verify', onVerified, embedded = false }) => {
  const user = useSelector(state => state.auth.user);
  const [mode, setMode] = useState(user?.is_2fa_enabled ? 'otp' : 'password');
  const [verifying, setVerifying] = useState(false);
  const [sending, setSending] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [sentTo, setSentTo] = useState(user?.email || '');
  const [otp, setOtp] = useState('');
  const [countdown, setCountdown] = useState(0);

  useEffect(() => {
    if (countdown <= 0) return undefined;
    const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);

  const handleSendCode = async () => {
    setSending(true);
    try {
      const result = await adminactions.requestVerificationOTP();
      if (result?.requires_password) {
        message.info(result.message);
        setMode('password');
        return;
      }
      if (result?.email) {
        setSentTo(result.email);
        setCodeSent(true);
        setOtp('');
        setCountdown(60);
        message.success(result.message || 'Verification code sent');
      } else {
        message.error(result?.message || 'Failed to send verification code');
      }
    } finally {
      setSending(false);
    }
  };

  const handleVerifyCode = async (code = otp) => {
    if (!code || code.length !== 6) {
      message.error('Please enter the 6-digit code');
      return;
    }
    setVerifying(true);
    try {
      const result = await adminactions.verifyOTPCode(code);
      if (result?.verified) {
        onVerified();
        return;
      }
      message.error(result?.message || 'Invalid verification code');
      setOtp('');
      if (result?.expired) setCodeSent(false);
      if (result?.requires_password) setMode('password');
    } catch {
      message.error('Verification failed');
    } finally {
      setVerifying(false);
    }
  };

  const handlePasswordSubmit = async (values) => {
    setVerifying(true);
    try {
      const result = await adminactions.verifyPassword(values.password);
      if (result?.verified) {
        onVerified();
      } else {
        message.error(result?.message || 'Incorrect password');
      }
    } catch {
      message.error('Verification failed');
    } finally {
      setVerifying(false);
    }
  };

  const renderOtp = () => (
    <>
      <div style={{ color: '#666', marginBottom: 24 }}>
        {codeSent
          ? <>Enter the 6-digit code sent to <strong>{maskEmail(sentTo)}</strong></>
          : 'Two-factor authentication is enabled. We will email you a verification code.'}
      </div>
      {codeSent ? (
        <>
          <div style={{ marginBottom: 24 }}>
            <Input.OTP
              length={6}
              size="large"
              value={otp}
              formatter={(str) => str.replace(/\D/g, '')}
              onChange={(value) => {
                setOtp(value);
                if (value.length === 6) handleVerifyCode(value);
              }}
              disabled={verifying}
            />
          </div>
          <Button type="primary" size="large" block loading={verifying} onClick={() => handleVerifyCode()}>
            {submitLabel}
          </Button>
          <Button type="link" onClick={handleSendCode} loading={sending} disabled={countdown > 0} style={{ marginTop: 8 }}>
            {countdown > 0 ? `Resend code in ${countdown}s` : 'Resend code'}
          </Button>
        </>
      ) : (
        <Button type="primary" size="large" block loading={sending} onClick={handleSendCode}>
          Send Verification Code
        </Button>
      )}
      <div>
        <Button type="link" onClick={() => setMode('password')}>
          Use password instead
        </Button>
      </div>
    </>
  );

  const renderPassword = () => (
    <>
      <div style={{ color: '#666', marginBottom: 24 }}>{description}</div>
      <Form onFinish={handlePasswordSubmit}>
        <Form.Item
          name="password"
          rules={[{ required: true, message: 'Please enter your password' }]}
        >
          <Input.Password
            prefix={<LockOutlined />}
            placeholder="Enter your password"
            size="large"
          />
        </Form.Item>
        <Form.Item style={{ marginBottom: 0 }}>
          <Button type="primary" htmlType="submit" size="large" loading={verifying} block>
            {submitLabel}
          </Button>
        </Form.Item>
      </Form>
      {user?.is_2fa_enabled && (
        <Button type="link" onClick={() => setMode('otp')} style={{ marginTop: 8 }}>
          Verify with 2FA code instead
        </Button>
      )}
    </>
  );

  const iconSize = embedded ? 36 : 48;
  const content = (
    <>
      {mode === 'otp'
        ? <SafetyOutlined style={{ fontSize: iconSize, color: '#1677ff', marginBottom: embedded ? 8 : 16 }} />
        : <LockOutlined style={{ fontSize: iconSize, color: '#faad14', marginBottom: embedded ? 8 : 16 }} />}
      {title && <div style={{ fontSize: 18, fontWeight: 500, marginBottom: 8 }}>{title}</div>}
      {mode === 'otp' ? renderOtp() : renderPassword()}
    </>
  );

  if (embedded) {
    return <div style={{ textAlign: 'center' }}>{content}</div>;
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', paddingTop: '60px' }}>
      <Card style={{ textAlign: 'center', padding: '40px 60px', maxWidth: 440 }}>
        {content}
      </Card>
    </div>
  );
};

export default VerifyIdentityCard;
